import React, { useState } from 'react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import { verifyTwoFactor } from '../../store/general/general-actions';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import { isHttpError } from '@common/interfaces/api/http-error';
import { selectTwoFactorRest } from 'store/general/general-slice-selectors';
import { resetTwoFactorRestState } from 'store/general/general-slice';

interface TwoFactorVerifyProps {
  pendingToken: string;
  onSuccess: () => void;
}

const TwoFactorVerify: React.FC<TwoFactorVerifyProps> = ({ pendingToken, onSuccess }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const twoFactorRest = useAppSelector((state) => selectTwoFactorRest(state));

  const [code, setCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);

  const isLocked =
    twoFactorRest.verifyTwoFactorPostError != null &&
    isHttpError(twoFactorRest.verifyTwoFactorPostError) &&
    twoFactorRest.verifyTwoFactorPostError.code === '423';

  const submitHandler = () => {
    dispatch(
      verifyTwoFactor({
        pendingToken,
        otp: useBackupCode ? undefined : code,
        backupCode: useBackupCode ? code : undefined,
      }),
    );
  };

  const toggleBackupCodeHandler = () => {
    setUseBackupCode((prev) => !prev);
    setCode('');
    dispatch(
      resetTwoFactorRestState({
        verifyTwoFactorPostError: null,
        verifyTwoFactorPostStatus: HttpFetchStatus.Idle,
      }),
    );
  };

  return (
    <Stack spacing={2} width="100%">
      <TextField
        label={useBackupCode ? t('input-label-backup-code') : t('input-label-authenticator-code')}
        value={code}
        onChange={(event) => setCode(event.target.value)}
        fullWidth
      />

      {twoFactorRest.verifyTwoFactorPostError != null &&
        isHttpError(twoFactorRest.verifyTwoFactorPostError) && (
          <CustomAlert
            header={isLocked ? t('error-two-factor-locked') : t('error-two-factor-invalid-code')}
            textMessages={[t(twoFactorRest.verifyTwoFactorPostError.message)]}
            severity="error"
          />
        )}

      <Button
        onClick={submitHandler}
        variant="contained"
        disabled={
          code.length === 0 ||
          isLocked ||
          twoFactorRest.verifyTwoFactorPostStatus === HttpFetchStatus.Loading
        }
      >
        {t('button-verify')}
      </Button>

      <Link component="button" onClick={toggleBackupCodeHandler}>
        {useBackupCode
          ? t('link-use-authenticator-code-instead')
          : t('link-use-backup-code-instead')}
      </Link>
    </Stack>
  );
};

export default TwoFactorVerify;
